import {useMemo} from 'react';

import {DataFrame} from 'data-frames/DataFrameTypes';
import {DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import {
  GetPersistedDataFrame,
  GetPersistedDataFrameVariables,
} from 'data-frames/wrapper/__generated/GetPersistedDataFrame';
import {GET_PERSISTED_DATA_FRAME_QUERY} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  GetScratchPlateDesignSummary,
  GetScratchPlateDesignSummary_scratchPlate_ScratchPlateNode as ScratchPlateNode,
  GetScratchPlateDesignSummaryVariables,
} from 'notebook/integrations/plate-maps/scratch-plates/__generated/GetScratchPlateDesignSummary';
import {
  DESTINATION_TRANSFER_LAYER_NAME,
  layerDataFrameId,
  PlateFieldValue,
  useScratchPlateData,
} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateDesignSummaryUtil';
import {
  PlateRecordHydratedDataQuery,
  PlateRecordHydratedDataQueryVariables,
} from 'samples/plate-maps/plate-record/__generated/PlateRecordHydratedDataQuery';
import {PLATE_RECORD_HYDRATED_DATA_QUERY} from 'samples/plate-maps/plate-record/PlateRecordQueryUtil';
import {hydratedPlateDataFromQueryResult} from 'samples/plate-maps/plate-record/PlateRecordUtil';
import {getEmptyPlateMapForScratchPlate} from 'samples/plate-maps/scratch-plates/ScratchPlateDesignModalUtil';

import {
  buildFilledPlateMap,
  buildHydrationVariablesFromContent,
  CONTENT_LAYER_NAME,
  FilledScratchPlateData,
  parseContentDataFrame,
} from './FilledScratchPlateUtil';

export type {FilledScratchPlateData};

/**
 * The hook leans on `BenchlingQueryResult` (loading / error /
 * inaccessible-plate) for the plate fetch, and reserves domain-specific
 * statuses for the cases the raw query state can't express.
 */
export type UseFilledScratchPlateDataResult =
  | {
      kind: 'query';
      query: SkippableBenchlingQueryResult<
        GetScratchPlateDesignSummary,
        GetScratchPlateDesignSummaryVariables
      >;
    }
  | {kind: 'missing-content-layer'}
  | {kind: 'hydration-error'}
  | {kind: 'unregistered-samples'}
  | {kind: 'success'; data: FilledScratchPlateData};

export interface UseFilledScratchPlateDataReturn {
  result: UseFilledScratchPlateDataResult;
  // Exposed so callers can render headers/inventory actions without re-running
  // `useScratchPlateData`, which this hook already calls.
  plate: ScratchPlateNode | null;
  // This plate record's own plate-level metadata fields (see `useScratchPlateData`).
  plateFields?: Array<PlateFieldValue>;
}

const EMPTY_HYDRATION_VARIABLES: PlateRecordHydratedDataQueryVariables = {
  bioentityIds: [],
  storableIds: [],
  unitIds: [],
  unitVersionNumbers: [],
  testOrderIds: [],
};

export default function useFilledScratchPlateData(scratchPlateId: string): UseFilledScratchPlateDataReturn {
  const {
    scratchPlateQuery,
    plate,
    plateMap,
    containerSchemaId,
    dataFrameIsInaccessible,
    dataFrameQueriesPending,
    dataFrameQueriesError,
    plateFields,
  } = useScratchPlateData(scratchPlateId);

  const isTransferPlate =
    plate?.layers.some((layer) => layer.name === DESTINATION_TRANSFER_LAYER_NAME) ?? false;
  const contentLayerDataFrameId = layerDataFrameId(plate?.layers.find((l) => l.name === CONTENT_LAYER_NAME));

  // TODO(BNCH-154328): paginate; today the parser
  // flags isTruncated and the viewer renders whatever placements we
  // received alongside a "some data may be missing" banner.
  const contentDataFrameQuery = useSkippableBenchlingQuery<
    GetPersistedDataFrame,
    GetPersistedDataFrameVariables
  >(GET_PERSISTED_DATA_FRAME_QUERY, {
    variables: {
      dataFrameId: contentLayerDataFrameId ?? '',
      limit: DEFAULT_DATA_FRAME_ROW_LIMIT,
    },
    skip: contentLayerDataFrameId == null,
  });

  const contentDataFrameNode =
    contentDataFrameQuery.type === QueryResultType.SUCCESS &&
    contentDataFrameQuery.data.dataFrame.__typename === 'DataFrameNode'
      ? contentDataFrameQuery.data.dataFrame
      : null;

  const contentDataFrameIsInaccessible =
    contentDataFrameQuery.type === QueryResultType.SUCCESS &&
    isInaccessible(contentDataFrameQuery.data.dataFrame);

  // `dataFrameJson` is JSONScalar (typed `unknown`); mirror the cast in PersistedDataFrameTableWrapper.
  const contentDataFrame: DataFrame | null =
    (contentDataFrameNode?.dataFrameJson as unknown as DataFrame | undefined) ?? null;

  // Use the data frame's authoritative row count for the truncation signal,
  // rather than comparing fetched-rows to the limit.
  const contentTotalRowCount = contentDataFrameNode?.numRows ?? null;

  const parsedContent = useMemo(() => {
    if (contentDataFrame == null || contentTotalRowCount == null) {
      return null;
    }
    return parseContentDataFrame(contentDataFrame, contentTotalRowCount);
  }, [contentDataFrame, contentTotalRowCount]);

  const hydrationVariables = useMemo(
    () =>
      parsedContent != null ? buildHydrationVariablesFromContent(parsedContent.contentByWellPosition) : null,
    [parsedContent]
  );

  const hydrationQuery = useSkippableBenchlingQuery<
    PlateRecordHydratedDataQuery,
    PlateRecordHydratedDataQueryVariables
  >(PLATE_RECORD_HYDRATED_DATA_QUERY, {
    variables: hydrationVariables ?? EMPTY_HYDRATION_VARIABLES,
    // Skip when there's nothing to hydrate (parse hasn't happened, or the parsed
    // content references no bioentities or units). The skipped case is treated
    // as SUCCESS-with-no-data downstream.
    skip:
      hydrationVariables == null ||
      (hydrationVariables.bioentityIds.length === 0 &&
        hydrationVariables.unitIds.length === 0 &&
        hydrationVariables.testOrderIds.length === 0),
  });

  // Pull `data` out under SUCCESS-narrowing so it can appear in the downstream
  // `useMemo` deps without re-narrowing.
  const hydrationData = hydrationQuery.type === QueryResultType.SUCCESS ? hydrationQuery.data : undefined;
  const plateHeight = plate?.height;
  const plateWidth = plate?.width;
  const basePlateMap = useMemo(
    () =>
      plateMap ??
      (plateHeight != null && plateWidth != null
        ? getEmptyPlateMapForScratchPlate(plateHeight, plateWidth)
        : null),
    [plateMap, plateHeight, plateWidth]
  );

  const result = useMemo<UseFilledScratchPlateDataResult>(() => {
    // Any unresolved fetch state is surfaced as the default query banner.
    if (
      scratchPlateQuery.type !== QueryResultType.SUCCESS ||
      isInaccessible(scratchPlateQuery.data.scratchPlate)
    ) {
      return {kind: 'query', query: scratchPlateQuery};
    }

    const hasDataFrameFailure =
      dataFrameIsInaccessible ||
      dataFrameQueriesError ||
      contentDataFrameIsInaccessible ||
      contentDataFrameQuery.type === QueryResultType.ERROR;
    const hasHydrationFailure = hasDataFrameFailure || hydrationQuery.type === QueryResultType.ERROR;

    const isBasePlatePending = plate == null || basePlateMap == null || dataFrameQueriesPending;
    const canContinueHydrationPipeline = contentLayerDataFrameId != null && !hasHydrationFailure;
    const isContentDataPending =
      canContinueHydrationPipeline && (contentDataFrame == null || parsedContent == null);
    const isHydrationPending =
      canContinueHydrationPipeline && hydrationQuery.type === QueryResultType.PENDING;
    if (isBasePlatePending || isContentDataPending || isHydrationPending) {
      return {kind: 'query', query: scratchPlateQuery};
    }

    // Different error/failure outcomes after query data has settled.
    if (hasHydrationFailure) {
      return {kind: 'hydration-error'};
    }
    if (contentLayerDataFrameId == null) {
      // Filled plates always carry a `content` layer; if this fires we
      // were handed a design plate (or a malformed filled plate).
      return {kind: 'missing-content-layer'};
    }
    if (parsedContent == null) {
      return {kind: 'query', query: scratchPlateQuery};
    }
    if (parsedContent.hasUnregisteredSamples) {
      // The viewer renders registered bioentities only.
      return {kind: 'unregistered-samples'};
    }

    // hydrationQuery.type ∈ {SUCCESS, SKIPPED}; SKIPPED is treated as
    // SUCCESS-with-no-data when nothing needed hydration.
    const hydrated =
      hydrationData != null
        ? hydratedPlateDataFromQueryResult(hydrationData)
        : {schemaIdsByReagentId: {}, unitsById: {}, testOrderInfoById: {}};

    return {
      kind: 'success',
      data: {
        plateName: plate.name,
        plateMap: buildFilledPlateMap({
          basePlateMap,
          contentByWellPosition: parsedContent.contentByWellPosition,
          schemaIdsByReagentId: hydrated.schemaIdsByReagentId,
          unitsById: hydrated.unitsById,
          testOrderInfoById: hydrated.testOrderInfoById,
        }),
        containerSchemaId,
        isTransferPlate,
        isTruncated: parsedContent.isTruncated,
      },
    };
  }, [
    scratchPlateQuery,
    plate,
    basePlateMap,
    containerSchemaId,
    isTransferPlate,
    dataFrameIsInaccessible,
    dataFrameQueriesPending,
    dataFrameQueriesError,
    contentLayerDataFrameId,
    contentDataFrameIsInaccessible,
    contentDataFrameQuery.type,
    contentDataFrame,
    parsedContent,
    hydrationQuery.type,
    hydrationData,
  ]);

  return useMemo(() => ({result, plate, plateFields}), [result, plate, plateFields]);
}
